import { DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, HostBinding, Inject, Input, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { CrmPromoIdsMap, EventModel, EventOptionGroup, SetTab } from '@frontend/sports/betting-offer/feature/model';
import {
    CrmConfig,
    EventDetailsConfig,
    MarketGroupingConfig,
    MarqueeConfig,
    SitecoreEventDetailsConfig,
} from '@frontend/sports/common/client-config-data-access';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { DetailedEventSubscriptionService } from '@frontend/sports/event-subscription/feature/detailed-event-subscription';
import { MediaEventSource, MediaModuleContextPage } from '@frontend/sports/grid/media/feature/model';
import { MediaApiService } from '@frontend/sports/grid/media/feature/state';
import { TrackingVideoSource } from '@frontend/sports/tracking/feature';
import { HtmlNode } from '@frontend/vanilla/core';
import { debounce, find, groupBy, isEqual, keyBy, sortBy } from 'lodash-es';
import { Observable } from 'rxjs';

import { BetBuilderPrecreatedModel } from '../betbuilder/model/bet-builder.model';
import { SourceOfPick } from '../betslip-base/models/pick-models';
import { EDPScoreboardConfigService } from '../common/edp-scoreboard-config.service';
import { Promotion } from '../crm-offer-data/crm-offer.model';
import { CrmPromotionMapperService } from '../crm-promotion-container/crm-promotion-mapper.service';
import { CrmPromotionProviderService, PromotionMap } from '../crm-promotion/crm-promotion-provider.service';
import { ComponentProxy } from '../deferred/component-loader.service';
import { DetailedEventViewModel, ModularEventViewModel, OptionMarketSetData } from '../event-details-common/event-details-common.models';
import { EventDetailsViewModelService } from '../event-details-common/event-details-view-model.service';
import { EventDetailsService } from '../event-details-common/event-details.service';
import { PlayerStatsSubscriptionService } from '../event-subscription/player-stats-subscription-service';
import { ScoreboardSubscriptionService } from '../event-subscription/scoreboard-subscription.service';
import { AutomatedMarqueeService } from '../highlights-marquee/automated-marquee.service';
import { HighlightsMarqueeComponentLoaderService } from '../highlights-marquee/highlights-marquee-component-loader.service';
import { HighlightsMarqueePage } from '../highlights-marquee/highlights-marquee-page.enum';
import type { HighlightsMarqueeComponent } from '../highlights-marquee/highlights-marquee.component';
import { Tile } from '../highlights-marquee/marquee-tile.model';
import { MarqueeService } from '../highlights-marquee/marquee.service';
import { EventViewBinding, MyBetsComponentLoaderService } from '../my-bets/my-bets-component-loader.service';
import { ExternalNativeAppService } from '../native-api/external-native-app.service';
import { NavigationRouterService } from '../navigation/navigation-router.service';
import { TemplatesWithThresholds } from '../option-group/option-group-list.component';
import { SheetViewService } from '../option-group/sheet-view.service';
import { TabControllerFacadeService } from '../option-sheet-view/store/tab-controller-facade.service';
import { PopupManager } from '../popup/popup-manager.service';
import { optionContextServiceProviderFactory } from '../shared-priceboost/Services/option-context.service.factory';
import { WidgetTrackingService } from '../widget/core/widget-tracking.service';
import { BetBuilderTrackingValues } from './bet-builder-v2/bet-builder-tracking';
import { BetBuilderOnboardingMessageService } from './bet-builder-v2/event-details-onboarding/bet-builder-onboarding-message.service';
import { NavigationStateService } from './navigation-state/navigation-state.service';

@HooksWireup()
@Component({
    selector: 'ms-event-details-main',
    templateUrl: 'event-details.component.html',
    providers: [optionContextServiceProviderFactory(true), EDPScoreboardConfigService, EventDetailsViewModelService],
})
export class EventDetailsComponent extends OnRouteResolve<DetailedEventViewModel> implements AfterViewInit, OnDestroy {
    @HostBinding('class') className = 'event-details';
    @HostBinding('class.poker-nativeapp') isPokerNativeApp = false;
    @HostBinding('class.has-error') hasError = false;
    @HostBinding('class.event-modular-enabled') isEventPageModularEnabled = this.eventDetailsConfig.enableEventPageModular;
    @HostBinding('class.hide-edp-breadcrumbs') hideEDPBreadcrumbs = false;

    @Input() set detailedEventModel(value: DetailedEventViewModel) {
        this.onRouteResolved(value);
    }

    sourceOfPick = SourceOfPick.EventDetails;
    event?: EventModel;
    promoted: number[];
    promotedMap: { [key: number]: number };
    page: HighlightsMarqueePage = HighlightsMarqueePage.EventDetails;

    crmOptionGroupsPromotedMap: { [key: string]: string };
    crmPromoIdsMap: CrmPromoIdsMap = { markets: {}, options: {}, templateIds: {} };
    crmBannerPromotions: Promotion[] = [];

    optionGroups: EventOptionGroup[];
    offset: number;

    setType = SetTab;
    setMain?: number | string;
    setId: string | number;
    subSet?: string;
    groupedSets?: number[];
    tags?: string[];
    isSgp?: boolean;
    defaultTab?: string;

    lastScrollTop = 0;
    isNewEDPScoreboard: boolean;

    preCreated?: BetBuilderPrecreatedModel;
    thresholds: TemplatesWithThresholds;

    disableAnimation = true;
    myBetsComponent: ComponentProxy<EventViewBinding>;
    totalOptionGroupsCount: number;
    defaultExpandedMarketsCount: number | undefined;
    eventModularVM: ModularEventViewModel;
    isEventPageModularEnabledWithWidgets = false;
    isEventSitemapEnabled = false;
    optionMarketSetData = signal<OptionMarketSetData>({});

    private marqueesEmpty = false;
    private hasMarqueeAvailable: boolean;
    private feature?: number;
    private crmPromotions: Promotion[] = [];
    private subscription?: EventSubscription<EventModel>;
    private isLive = false;
    marqueeComponent: ComponentProxy<HighlightsMarqueeComponent>;
    onboardingVisible$: Observable<boolean>;
    private isPrecreatedBABTabActive: boolean;
    source: string;
    private tabControllerFacadeService = inject(TabControllerFacadeService);
    private sheetViewService = inject(SheetViewService);

    get isMarqueeVisible(): boolean {
        return this.isMainOrAllTab && this.hasMarqueeAvailable && !this.marqueesEmpty && !this.isEventPageModularEnabled;
    }

    get isPromotionContainerVisible(): boolean {
        return !!this.crmBannerPromotions.length && !this.isMarqueeVisible && this.isMainOrAllTab && !this.isEventPageModularEnabled;
    }

    get isPopupShown(): boolean {
        return this.popupManager.isOpen();
    }

    constructor(
        public sitecoreEventDetails: SitecoreEventDetailsConfig,
        private timer: TimerService,
        private popupManager: PopupManager,
        private navigationRouter: NavigationRouterService,
        private navigationStateService: NavigationStateService,
        private scrollContainer: ScrollContainerService,
        private eventSubscriptionService: DetailedEventSubscriptionService,
        private scoreboardSubscriptionService: ScoreboardSubscriptionService<EventModel>,
        private helper: EventDetailsService,
        private promotionMapper: CrmPromotionMapperService,
        private promotionProvider: CrmPromotionProviderService,
        private promotionConfig: CrmConfig,
        private marqueeConfig: MarqueeConfig,
        private marqueeService: MarqueeService,
        private eventDetailsConfig: EventDetailsConfig,
        private marketGroupingConfig: MarketGroupingConfig,
        private mediaApi: MediaApiService,
        private externalNativeAppService: ExternalNativeAppService,
        private changeRef: ChangeDetectorRef,
        private automatedMarqueeService: AutomatedMarqueeService,
        loader: MyBetsComponentLoaderService,
        route: ActivatedRoute,
        private marqueeLoader: HighlightsMarqueeComponentLoaderService,
        private widgetTrackingService: WidgetTrackingService,
        private eventDetailsModelService: EventDetailsViewModelService,
        private htmlNode: HtmlNode,
        private edpScoreboardConfigService: EDPScoreboardConfigService,
        private onboardingMessageService: BetBuilderOnboardingMessageService,
        private playerStatsSubscriptionService: PlayerStatsSubscriptionService<EventModel>,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {
        super(route);

        this.myBetsComponent = loader.getEventViewComponent();
        this.isPokerNativeApp = this.externalNativeAppService.isPokerNativeApp();
        this.isEventSitemapEnabled = this.eventDetailsConfig.enableSitemapNavigation;

        if (this.eventDetailsConfig.defaultExpandedMarketsCount > 0) {
            this.defaultExpandedMarketsCount = this.eventDetailsConfig.defaultExpandedMarketsCount;
        }

        if (!this.isEventPageModularEnabled) {
            this.marqueeComponent = this.marqueeLoader.getHighlightsMarqueeComponent();
        }

        this.isNewEDPScoreboard = this.edpScoreboardConfigService.isNewEDPScoreboardEnabled();

        if (this.eventDetailsConfig.enableHideBreadcrumbOnScroll && this.isNewEDPScoreboard) {
            this.scrollContainer.scroll.pipe(takeUntilDestroyed()).subscribe(() => {
                this.onScrollHandler();
            });
        }

        this.source = BetBuilderTrackingValues.EventDetails;
    }

    ngOnDestroy(): void {
        this.navigationRouter.resetNavigation();
        this.subscription?.unsubscribe();
        this.setMediaModuleContext(MediaModuleContextPage.Default, null);
        this.clearTabControllerState();
    }

    clearTabControllerState() {
        if (this.sheetViewService.isEnabled) this.tabControllerFacadeService.resetTabControllerState();
    }

    onRouteResolved(model: DetailedEventViewModel): void {
        if (!model) {
            return;
        }

        this.optionGroups = [];
        if (!this.setId && this.setId !== 0) {
            this.setId = -100;
        }

        this.event = model.model;
        this.subscription?.unsubscribe();
        this.clearTabControllerState();
        this.hasError = !this.event?.published || this.event?.isFinished();

        if (!this.event) {
            return;
        }

        const config = this.eventDetailsConfig.marketTemplatesWithThresholds[this.event.sport.id];
        const templates = config ? keyBy(config, (id) => id) : {};
        this.thresholds = { templates, thresholds: this.marketGroupingConfig.optionCollapseSize };

        this.marqueesEmpty = false;
        this.setMediaModuleContext(MediaModuleContextPage.EventDetails, this.event.id);
        this.setEvent(this.event);

        this.promoted = model.promotedTemplate;
        this.preCreated = model.preCreated;

        this.setPromoted(this.event);
        this.setModel();
        if (this.isEventPageModularEnabled && model.widgetLayoutResponse?.widgets?.length) {
            this.eventDetailsModelService.setDetailedEventViewModel(model);
            this.eventModularVM = this.mapModularVm(model);
            this.widgetTrackingService.trackWidgetsLoaded(model.widgetLayoutResponse);
            this.isEventPageModularEnabledWithWidgets = true;
        }

        this.isLive = this.event.live;
        if (this.isLive) {
            this.hasMarqueeAvailable = false;
        }

        if (this.isLive && !this.navigationStateService.isPrematchNavRegistered) {
            this.navigationRouter.setNavigation('live');
        } else {
            this.navigationRouter.setNavigation(this.event.sport.id.toString());
        }
        if (!this.isEventPageModularEnabled) {
            this.hasMarqueeAvailable = model.hasMarqueeAvailable && this.marqueeConfig.isHighlightsMarqueeEnabled;
            let marqueeTiles: Tile[] = [];
            if (this.hasMarqueeAvailable) {
                this.marqueeService
                    .loadMarquees(this.event.id)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((newMarqueeTiles) => {
                        marqueeTiles = [...this.getAutomatedMarqueeTiles(model, newMarqueeTiles)];
                        this.marqueesEmpty = !marqueeTiles.length;
                        this.marqueeComponent.update({ tiles: marqueeTiles });
                    });
            } else {
                marqueeTiles = this.marqueeConfig.isHighlightsMarqueeEnabled ? [...this.getAutomatedMarqueeTiles(model, marqueeTiles)] : [];
                this.marqueesEmpty = !marqueeTiles.length;
                this.marqueeComponent.update({ tiles: marqueeTiles });
            }

            this.promotionProvider.promotions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((promotions) => this.promotionChanged(promotions));
        } else {
            this.promotionProvider.promotions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((promotions) => {
                this.crmPromotions = this.event ? this.promotionMapper.getPromotionsForEvent(this.event, promotions) : [];
                this.crmOptionGroupsPromotedMap = this.promotionMapper.getPromotedOptionGroupIds(this.optionGroups, this.event!, this.crmPromotions);
            });
        }

        this.subscription = this.eventSubscriptionService.subscribe([
            {
                event: this.event,
                /**
                 * Performance optimization:
                 * When push messages are received in close succession this reduces amount of eventChanged executions
                 * to a single call and updates component model in one go instead of updating it after every message
                 */
                callback: debounce(this.eventChanged, 100),
            },
        ]);
        this.subscription.add(this.scoreboardSubscriptionService.subscribe([{ event: this.event }]));
        this.subscription.add(this.playerStatsSubscriptionService.subscribe([{ event: this.event, callback: this.eventChanged }]));

        this.promotionProvider.promotionsMap$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ids) => this.promoIdsChanged(ids));
    }

    ngAfterViewInit(): void {
        this.timer.setTimeout(() => (this.disableAnimation = false));

        const modalBody = this.document.querySelector<HTMLElement>('.modal-body:has(.event-details)');
        if (modalBody) {
            this.scrollContainer.setTarget(modalBody);
        }
    }

    onScrollHandler(): void {
        if (this.scrollContainer.scrollTop > 0 && this.scrollContainer.scrollTop > this.lastScrollTop) {
            this.hideEDPBreadcrumbs = true;
            this.htmlNode.setCssClass('hide-edp-header', true);
        } else {
            this.hideEDPBreadcrumbs = false;
            this.htmlNode.setCssClass('hide-edp-header', false);
        }
        this.lastScrollTop = this.scrollContainer.scrollTop;
    }

    setChanged(setData: {
        setId: string | number;
        groupedSetsIds?: number[];
        subSetId?: string;
        tags?: string[];
        isSgp?: boolean;
        defaultTab?: string;
    }): void {
        if (this.event) {
            this.onboardingVisible$ = this.onboardingMessageService.visible$(this.event, setData.setId === SetTab.BetBuilder);
        }

        if (this.setId === setData.setId && setData.subSetId === this.subSet && isEqual(this.tags, setData.tags)) {
            return;
        }

        this.setId = setData.setId;
        this.subSet = setData.subSetId;
        this.groupedSets = setData.groupedSetsIds;
        this.tags = setData.tags;
        this.isSgp = setData.isSgp;
        this.defaultTab = setData.defaultTab;
        this.setModel();

        if (this.event) {
            this.setMain = this.getMainSet(this.event);
        }

        if (this.isMainOrAllTab) {
            this.isEventPageModularEnabledWithWidgets = this.eventModularVM?.widgetPositions.length > 0;
        } else {
            this.isEventPageModularEnabledWithWidgets = false;
        }

        if (this.setId === SetTab.MyBets && this.event) {
            this.myBetsComponent.update({ event: this.event });
        }

        this.scrollContainer.scrollTo(0, 0);
    }

    featureChanged(feature: number): void {
        const current = this.feature;
        this.feature = feature;

        // we check whether there is some change to overcome possible reflow
        if (current !== undefined && current !== feature) {
            this.scrollContainer.scrollTo(0, 0);
        }
    }

    private promoIdsChanged(promoMap: PromotionMap): void {
        this.crmPromoIdsMap = {
            templateIds: promoMap.templateIds,
            markets: promoMap.markets,
            options: promoMap.options,
        };
    }

    private promotionChanged(model: Promotion[]): void {
        this.crmPromotions = this.event ? this.promotionMapper.getPromotionsForEvent(this.event, model) : [];
        this.setCrmPromotions();
    }

    private setCrmPromotions(): void {
        if (!this.event) {
            return;
        }

        this.crmOptionGroupsPromotedMap = this.promotionMapper.getPromotedOptionGroupIds(this.optionGroups, this.event, this.crmPromotions);
        this.crmBannerPromotions = this.promotionMapper
            .getBannerPromotions(this.event.optionGroups, this.event, this.crmPromotions)
            .slice(0, this.promotionConfig.promotions.bannerCount.eventView);
        this.marqueeComponent.update({
            promotionTiles: this.crmBannerPromotions,
            preventNavigation: true,
            showLoader: true,
            page: this.page,
        });
    }

    private setModel(): void {
        if (this.setId === undefined || this.setId === -100 || !this.event) {
            return;
        }
        this.isPrecreatedBABTabActive = this.helper.isPreCreatedBABTab(this.event, this.setId);

        this.sourceOfPick = this.isSgp ? SourceOfPick.EmbeddedBetBuilder : SourceOfPick.EventDetails;

        const setData: OptionMarketSetData = {
            activeTab: this.setId,
            groupedSetsIds: this.groupedSets,
            tag: this.subSet,
            setTags: this.tags,
            isSgp: this.isSgp,
            isPrecreatedBABTab: this.isPrecreatedBABTabActive,
        };
        this.optionMarketSetData.set(setData);

        const optionGroups = this.helper.prepareEventOptionGroups({
            event: this.event,
            ...setData,
        });

        if (this.setId === this.setType.All) {
            this.optionGroups = [];

            this.event.optionSets.forEach(
                (set) =>
                    (this.optionGroups = this.optionGroups.concat(
                        sortBy(
                            optionGroups.filter((group) => group.detailedGrouping.marketSet === set.id),
                            (optionGroup) => !(optionGroup.templateId in this.promotedMap),
                            (optionGroup) => optionGroup.detailedGrouping.marketOrder,
                        ),
                    )),
            );

            this.crmOptionGroupsPromotedMap = this.promotionMapper.getPromotedOptionGroupIds(this.optionGroups, this.event, this.crmPromotions);

            return;
        }

        this.optionGroups = sortBy(
            optionGroups,
            (optionGroup) => !(optionGroup.templateId in this.promotedMap),
            (optionGroup) => (this.setId === this.setType.MyMarkets ? optionGroup.position : optionGroup.detailedGrouping.marketOrder),
        );

        this.totalOptionGroupsCount = this.helper.calculateEventOptionGroupsCount(this.event);
        if (!this.isEventPageModularEnabled) {
            this.setCrmPromotions();
        }
    }

    private eventChanged = () => {
        if (this.event) {
            const setMediaEvent = this.event.live !== this.isLive;
            this.isLive = this.event.live;

            if (this.isLive) {
                this.hasMarqueeAvailable = false;
                if (this.setId === this.setType.BetBuilder) {
                    this.setChanged({ setId: 0, subSetId: undefined, defaultTab: '' });
                }
            }
            if (setMediaEvent) {
                this.setEvent(this.event);
            }
        }

        this.setModel();
        this.changeRef.markForCheck();
    };

    private getMainSet(event: EventModel): number | string | undefined {
        return this.eventDetailsConfig.enableSitemapNavigation
            ? this.defaultTab
            : (event.optionSets.map((optionSet) => optionSet.id).shift() as number);
    }

    private setPromoted(event: EventModel): void {
        this.promotedMap = keyBy(this.promoted || [], (promoted) => promoted);

        if (this.promoted && this.promoted.length > 0) {
            const promotedOptionGroup = find(event.optionGroups, (optionGroup) => optionGroup.templateId in this.promotedMap);

            if (promotedOptionGroup) {
                this.setId = promotedOptionGroup.detailedGrouping.marketSet;
            }
        }
    }

    private setEvent(event: EventModel): void {
        this.mediaApi.setEvent(event, MediaEventSource.EventDetails, null, TrackingVideoSource.EventDetails);
    }

    private setMediaModuleContext(mediaModuleContext: MediaModuleContextPage, eventId: string | null): void {
        this.mediaApi.setMediaWidgetContext(mediaModuleContext, eventId);
    }

    get isMainOrAllTab(): boolean {
        return this.setId === SetTab.All || this.setId === this.setMain;
    }

    defaultExpandedMarkets(widgetPosition?: number): number | undefined {
        if (!this.defaultExpandedMarketsCount || this.isPrecreatedBABTabActive) {
            return;
        }

        if (widgetPosition) {
            return Math.max(this.defaultExpandedMarketsCount - widgetPosition, 0);
        }

        return this.defaultExpandedMarketsCount;
    }

    trackPosition(index: number): number {
        return index;
    }

    getTracking(): string {
        return this.sourceOfPick === SourceOfPick.EmbeddedBetBuilder ? 'betbuilder' : 'EventDetails/Sport/' + this.event?.sport?.id;
    }

    private getAutomatedMarqueeTiles(model: DetailedEventViewModel, marqueeTiles: Tile[]): Tile[] {
        if (marqueeTiles.length < this.marqueeConfig.maxTiles && this.marqueeConfig.isAutomatedMarqueesEnabled && model.fixture) {
            const automatedMarqueeTiles = this.automatedMarqueeService.createAutomatedMarqueeTiles(model);
            const automatedTiles = this.automatedMarqueeService.getTiles(
                model?.fixture,
                automatedMarqueeTiles,
                model.betBuilderFixture?.sourceFixture,
                model.betBuilderFixture?.precreatedMarketsInfo,
            );
            if (automatedTiles.length > 0 && !this.isLive) {
                this.hasMarqueeAvailable = true;
            }

            return this.automatedMarqueeService.getCombinedTiles(marqueeTiles, automatedTiles);
        }

        return marqueeTiles;
    }

    private mapModularVm(model: DetailedEventViewModel): ModularEventViewModel {
        const groupedWidgets = groupBy(model.widgetLayoutResponse?.widgets, (group) => group.position);
        const widgetPositions = Object.keys(groupedWidgets).map((m) => Number(m));

        return {
            layoutTemplate: model.widgetLayoutResponse?.template,
            widgetPage: model.widgetLayoutResponse?.page,
            widgetPositions,
            groupedWidgets,
        };
    }
}
